import React, { useState, useEffect, useRef } from "react";
import { FiUserPlus, FiX, FiCheck } from "react-icons/fi";
import "./AssignmentContextMenu.css";

const AssignmentContextMenu = ({ 
  x, 
  y, 
  personId,
  assignmentType, // 'primary' or 'secondary'
  currentAssignments = [],
  availableUsers = [],
  onClose,
  onAddAssignment,
  onRemoveAssignment,
  showToast
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Get assigned user IDs across both types to prevent duplicate assignments
  const assignedUserIds = currentAssignments.map(a => a.user_id);
  
  // Filter available users that aren't already assigned
  const unassignedUsers = availableUsers.filter(user => 
    !assignedUserIds.includes(user.id)
  );

  // Filter by search term
  const filteredUsers = unassignedUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(searchLower);
  });

  const handleAddUser = async (userId) => {
    try {
      await onAddAssignment(personId, userId, assignmentType);
      setSearchTerm("");
    } catch (error) {
      console.error("Error adding assignment:", error);
      if (showToast) showToast("Failed to add assignment", "error");
    }
  };

  const handleRemoveUser = async (assignmentId) => {
    try {
      await onRemoveAssignment(assignmentId);
    } catch (error) {
      console.error("Error removing assignment:", error);
      if (showToast) showToast("Failed to remove assignment", "error");
    }
  };

  // Position the menu, making sure it stays on screen
  const menuStyle = {
    position: "fixed",
    left: Math.min(x, window.innerWidth - 320),
    top: Math.min(y, window.innerHeight - 400),
    maxHeight: "400px",
  };

  const typeLabel = assignmentType === 'primary' ? 'Primary' : 'Secondary';

  return (
    <div 
      ref={menuRef}
      className="assignment-context-menu" 
      style={menuStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="assignment-context-header">
        <h4>{typeLabel} Users</h4>
        <button className="close-btn" onClick={onClose} title="Close">
          <FiX size={16} />
        </button>
      </div>

      {/* Current Assignments */}
      {currentAssignments.length > 0 && (
        <div className="assignment-context-section">
          <div className="assignment-context-subtitle">Assigned Users:</div>
          <div className="assignment-context-list">
            {currentAssignments.map(assignment => {
              const isInactive = assignment.active === "n";
              const chipColor = isInactive ? "#F08080" : (assignment.chip_color || "#e0e0e0");
              
              return (
                <div 
                  key={assignment.assignment_id} 
                  className="assignment-context-item assigned"
                >
                  <div 
                    className="assignment-context-chip" 
                    style={{ backgroundColor: chipColor }}
                  />
                  <span className="assignment-context-name">
                    {assignment.first_name} {assignment.last_name}
                    {isInactive && <span className="inactive-label"> (Inactive)</span>}
                  </span>
                  <button
                    className="assignment-context-remove"
                    onClick={() => handleRemoveUser(assignment.assignment_id)}
                    title="Remove assignment"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add New Assignment */}
      <div className="assignment-context-section">
        <div className="assignment-context-subtitle">
          <FiUserPlus size={14} style={{ marginRight: "4px" }} />
          Add User:
        </div>
        <input
          type="text"
          className="assignment-context-search"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
        
        <div className="assignment-context-list">
          {filteredUsers.length === 0 ? (
            <div className="assignment-context-empty">
              {searchTerm ? "No matching users found" : "All users are assigned"}
            </div>
          ) : (
            filteredUsers.map(user => {
              const isInactive = user.active === "n";
              const chipColor = isInactive ? "#F08080" : (user.chip_color || "#e0e0e0");
              
              return (
                <div 
                  key={user.id} 
                  className="assignment-context-item available"
                  onClick={() => handleAddUser(user.id)}
                >
                  <div 
                    className="assignment-context-chip" 
                    style={{ backgroundColor: chipColor }}
                  />
                  <span className="assignment-context-name">
                    {user.first_name} {user.last_name}
                    {isInactive && <span className="inactive-label"> (Inactive)</span>}
                  </span>
                  <div className="assignment-context-add">
                    <FiCheck size={14} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentContextMenu;

